/**
 * Product Registry — Single source of truth for handle → productType mapping.
 *
 * When adding a new Shopify product:
 * 1. Add its handle here in HANDLE_TO_TYPE
 * 2. (Optional) Add dedicated content in productContent.ts
 * 3. (Optional) Add translations in i18n.ts / productContentTranslations.ts
 * 4. (Optional) Add local images in productImages.ts
 * 5. (Optional) Add fixed prices in fixedPrices.ts
 *
 * Products without an entry here will use keyword fallback, then 'default'.
 */

export type ProductType = 'masks' | 'serum' | 'cream' | 'bundle' | string;

// Exact handle → productType mapping
const HANDLE_TO_TYPE: Record<string, ProductType> = {
  'pdrn-pink-sheet-masks-10-pack': 'masks',
  'pink-glow-serum-with-pdrn-peptides': 'serum',
  'smart-capsule-cream-with-collagen': 'cream',
  'complete-pdrn-trio-bundle': 'bundle',
};

// Keyword fallback for backwards compatibility (e.g. variant handles)
function keywordFallback(handle: string): ProductType {
  if (handle.includes('mask')) return 'masks';
  if (handle.includes('serum') || handle.includes('glow')) return 'serum';
  if (handle.includes('cream') || handle.includes('capsule')) return 'cream';
  if (handle.includes('trio') || handle.includes('bundle') || handle.includes('set')) return 'bundle';
  return 'default';
}

/**
 * Returns the product type for a given Shopify handle.
 * Priority: exact match → keyword fallback → 'default'
 */
export function getProductType(handle: string): ProductType {
  return HANDLE_TO_TYPE[handle] ?? keywordFallback(handle);
}

/**
 * Register a new product handle at runtime (useful for dynamic registration).
 * Typically you'd just add to HANDLE_TO_TYPE above.
 */
export function registerProduct(handle: string, type: ProductType): void {
  HANDLE_TO_TYPE[handle] = type;
}
